/**
 * Hosted checkout redirect for Shopify storefronts.
 *
 * This asset runs in the Shopify theme only. It never holds Whop or Shopify
 * Admin secrets; it only reads the public Shopify cart and forwards a compact
 * cart payload to the hosted checkout app.
 *
 * UpCart support: UpCart renders its drawer inside a shadow root, so clicks are
 * retargeted to the shadow host before our document-level listeners see them,
 * and document.querySelectorAll / MutationObserver never reach inside. We
 * resolve clicks through composedPath(), query both roots, and observe the
 * shadow root separately.
 */
(function () {
  var CONFIG = window.ML_WHOP_CHECKOUT_CONFIG || window.WHOP_CHECKOUT_CONFIG || {};
  var CHECKOUT_BASE_URL = String(
    CONFIG.checkoutUrl ||
    CONFIG.checkoutBaseUrl ||
    CONFIG.hostedCheckoutUrl ||
    ''
  ).replace(/\/$/, '');
  if (CONFIG.enabled === false || !CHECKOUT_BASE_URL) return;
  var isOpening = false;
  var openingStartedAt = 0;
  var CART_ATTEMPTS = 12;
  var CHECKOUT_VERSION = '20260915-cart-token-upcart-1';

  var CHECKOUT_SELECTORS = [
    'button[name="checkout"]',
    'button[type="submit"][name="checkout"]',
    'input[name="checkout"]',
    'input[type="submit"][name="checkout"]',
    'a[href="/checkout"]',
    'a[href^="/checkout?"]',
    '#checkout',
    '.cart__checkout-button',
    '[form="cart"][name="checkout"]',
    '[data-cart-checkout]',
    '[data-checkout-button]',
    '[data-testid*="checkout" i]',
    '[aria-label*="checkout" i]',
    '[aria-label*="afrekenen" i]',
    '[aria-label*="pagamento" i]',
    '[aria-label*="cassa" i]',
    // UpCart (cart drawer app). Class names differ between UpCart cart V1 and
    // V2, so match on substring rather than an exact class.
    '#upcart-checkout-button',
    '[class*="upcart-checkout-button"]',
    '[class*="upcart"][class*="checkout"]',
    '[data-upcart-checkout]'
  ].join(',');

  var CART_ITEM_SELECTORS = [
    '.m-cart-item',
    '[data-cart-item]',
    '[class*="upcart-line-item"]',
    '[class*="upcart-item"]'
  ].join(',');

  var CART_READY_SELECTORS = [
    '.ymq-cart-app-checkout-button',
    '.ymq-cart-app-total',
    '.cart-item',
    '.m-cart-item',
    '[data-cart-item]',
    '[class*="upcart-cart"]',
    '[class*="upcart-line-item"]'
  ].join(',');

  /* ---------------------------------------------------------------------
   * Shadow DOM helpers
   * ------------------------------------------------------------------- */

  function upcartRoot() {
    var root = window.upcartDocumentOrShadowRoot;
    if (!root || root === document) return null;
    if (typeof root.querySelectorAll !== 'function') return null;
    return root;
  }

  /**
   * querySelectorAll across the main document AND UpCart's shadow root.
   * Returns a plain array so callers can always use forEach/length.
   */
  function queryAll(selector) {
    var out = [];
    var roots = [document, upcartRoot()];
    for (var i = 0; i < roots.length; i++) {
      if (!roots[i]) continue;
      try {
        var found = roots[i].querySelectorAll(selector);
        for (var j = 0; j < found.length; j++) out.push(found[j]);
      } catch (e) { /* selector unsupported in this root, skip */ }
    }
    return out;
  }

  /**
   * Resolve the real clicked element, crossing shadow boundaries.
   *
   * evt.target is retargeted to the shadow host for clicks originating inside
   * a shadow tree, which is why a plain evt.target.closest() misses UpCart's
   * checkout button entirely. composedPath() gives us the un-retargeted path.
   */
  function composedTarget(evt, selector) {
    var path = (evt && typeof evt.composedPath === 'function') ? evt.composedPath() : null;

    if (!path || !path.length) {
      if (!evt || !evt.target || !evt.target.closest) return null;
      return evt.target.closest(selector) || findCheckoutTextTarget(evt.target);
    }

    for (var i = 0; i < path.length; i++) {
      var node = path[i];
      if (node && node.nodeType === 1 && node.matches) {
        try {
          if (node.matches(selector)) return node;
        } catch (e) { /* ignore */ }
      }
    }

    // Nothing matched a known selector: fall back to the text heuristic on the
    // innermost few elements of the path.
    for (var k = 0; k < path.length && k < 8; k++) {
      if (path[k] && path[k].nodeType === 1) {
        var hit = findCheckoutTextTarget(path[k]);
        if (hit) return hit;
      }
    }

    return null;
  }

  function hasText(value, needle) {
    return String(value || '').toLowerCase().indexOf(needle) !== -1;
  }

  function findCheckoutTextTarget(el) {
    if (!el || !el.closest) return null;
    var target = el.closest('button, input, a');
    if (!target) return null;
    var text = String(target.innerText || target.value || target.getAttribute('aria-label') || '').toLowerCase();
    var href = target.getAttribute && target.getAttribute('href');
    var name = target.getAttribute && target.getAttribute('name');
    var id = target.getAttribute && target.getAttribute('id');
    if (name === 'checkout' || id === 'checkout') return target;
    if (href === '/checkout' || String(href || '').indexOf('/checkout?') === 0) return target;
    if (
      (
        hasText(text, 'checkout') ||
        hasText(text, 'afrekenen') ||
        hasText(text, 'secure checkout') ||
        hasText(text, 'check out') ||
        hasText(text, 'pagamento') ||
        hasText(text, 'cassa')
      ) &&
      (
        target.closest('form, .cart, .drawer, [class*="cart"], [id*="cart"]') ||
        // Inside UpCart's shadow tree there is no light-DOM cart ancestor to
        // find, so accept the button if it lives under an upcart container.
        target.closest('[class*="upcart"]')
      )
    ) {
      return target;
    }
    return null;
  }

  function normalizeImage(url) {
    if (!url) return null;
    if (String(url).indexOf('//') === 0) return window.location.protocol + String(url);
    return String(url);
  }

  /**
   * Money as written on the page, in cents.
   *
   * The separators have to be told apart rather than swapped blindly. This
   * replaced only the first comma and called the result a decimal point, so
   * "£1,234.56" became "1.234.56", parsed as 1.234, and charged £1.23 — and
   * every European "€1.234,56" broke the same way. Anything over a thousand
   * was wrong by a factor of a thousand.
   *
   * The rule: whichever separator comes last is the decimal point, and only
   * if one or two digits follow it. "1,234" is a thousand; "12,34" is not.
   */
  function centsFromMoney(text) {
    var cleaned = String(text || '').replace(/[^0-9.,]/g, '');
    if (!cleaned) return 0;

    var decimalAt = Math.max(cleaned.lastIndexOf('.'), cleaned.lastIndexOf(','));
    var following = decimalAt === -1 ? -1 : cleaned.length - decimalAt - 1;

    var whole = cleaned;
    var fraction = '';
    if (following === 1 || following === 2) {
      whole = cleaned.slice(0, decimalAt);
      fraction = cleaned.slice(decimalAt + 1);
    }

    // Every remaining separator is a thousands mark.
    whole = whole.replace(/[.,]/g, '');
    var cents = Number(whole || '0') * 100 + Number((fraction + '00').slice(0, 2));
    return isFinite(cents) ? Math.round(cents) : 0;
  }

  /**
   * The variant a cart row is for.
   *
   * The hosted checkout re-prices every line against Shopify before charging,
   * and it can only do that by variant id. Without one the server sees an
   * empty cart and refuses — so this digs through the several places themes
   * put it before the row is given up on.
   */
  function variantIdFromRow(row) {
    if (!row) return null;

    var direct =
      row.getAttribute('data-variant-id') ||
      row.getAttribute('data-variant') ||
      row.getAttribute('data-id');
    if (direct && /^\d+$/.test(String(direct).trim())) return String(direct).trim();

    var nested = row.querySelector('[data-variant-id], [data-variant]');
    if (nested) {
      var value = nested.getAttribute('data-variant-id') || nested.getAttribute('data-variant');
      if (value && /^\d+$/.test(String(value).trim())) return String(value).trim();
    }

    // "?variant=123" on the product link.
    var link = row.querySelector('a[href*="variant="]');
    if (link) {
      var fromHref = String(link.getAttribute('href') || '').match(/[?&]variant=(\d+)/);
      if (fromHref) return fromHref[1];
    }

    // Quantity inputs are commonly named updates[123] or id=123.
    var input = row.querySelector('input[name^="updates["], input[name="id"], input[data-quantity-variant-id]');
    if (input) {
      var name = String(input.getAttribute('name') || '');
      var fromName = name.match(/updates\[(\d+)\]/);
      if (fromName) return fromName[1];
      var byAttr = input.getAttribute('data-quantity-variant-id');
      if (byAttr && /^\d+$/.test(byAttr)) return byAttr;
      if (name === 'id' && /^\d+$/.test(String(input.value || '').trim())) {
        return String(input.value).trim();
      }
    }

    // Line item keys look like "123456789:abcdef0123456789".
    var keyed = row.querySelector('[data-line-item-key], [data-key]');
    var key = keyed
      ? keyed.getAttribute('data-line-item-key') || keyed.getAttribute('data-key')
      : row.getAttribute('data-key');
    if (key) {
      var fromKey = String(key).match(/^(\d+):/);
      if (fromKey) return fromKey[1];
    }

    return null;
  }

  function getCartFromDom() {
    var rows = queryAll(CART_ITEM_SELECTORS);
    var items = rows.map(function (row, idx) {
      var titleEl = row.querySelector('a[href*="/products/"], .m-cart-item__title, [data-cart-item-title], [class*="upcart-item-title"]');
      var qtyEl = row.querySelector('input[type="number"], input[name*="quantity"]');
      var imageEl = row.querySelector('img');
      var moneyEls = Array.prototype.slice.call(row.querySelectorAll('*')).filter(function (el) {
        return /(?:\$|€|£|USD|EUR|GBP)\s*\d/i.test(el.textContent || '');
      });
      var priceText = moneyEls.length ? moneyEls[moneyEls.length - 1].textContent : '';
      var quantity = qtyEl ? parseInt(qtyEl.value || '1', 10) : 1;
      var linePrice = centsFromMoney(priceText);
      return {
        variant_id: variantIdFromRow(row),
        quantity: quantity || 1,
        title: (titleEl && titleEl.textContent || 'Cart item ' + (idx + 1)).trim(),
        sku: null,
        price: quantity ? Math.round(linePrice / quantity) : linePrice,
        final_line_price: linePrice,
        image: imageEl ? imageEl.currentSrc || imageEl.src : null
      };
    }).filter(function (item) { return item.final_line_price > 0; });

    var totalPrice = items.reduce(function (sum, item) { return sum + item.final_line_price; }, 0);
    if (!items.length || !totalPrice) throw new Error('Could not read cart from page');

    return {
      currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || CONFIG.currency || 'USD',
      item_count: items.reduce(function (sum, item) { return sum + item.quantity; }, 0),
      total_price: totalPrice,
      items: items
    };
  }

  function sleep(ms) {
    return new Promise(function (resolve) {
      window.setTimeout(resolve, ms);
    });
  }

  function cartHasItems(cart) {
    return Boolean(
      cart &&
      Number(cart.item_count || 0) > 0 &&
      Array.isArray(cart.items) &&
      cart.items.length > 0 &&
      Number(cart.total_price || 0) > 0
    );
  }

  function readShopifyCart() {
    var controller = window.AbortController ? new AbortController() : null;
    var timeout = controller ? window.setTimeout(function () {
      controller.abort();
    }, 2500) : null;
    return fetch('/cart.js', {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
      signal: controller ? controller.signal : undefined
    }).then(function (r) {
      if (timeout) window.clearTimeout(timeout);
      if (!r.ok) throw new Error('Could not read Shopify cart');
      return r.json();
    }).catch(function (error) {
      if (timeout) window.clearTimeout(timeout);
      throw error;
    });
  }

  function getCart(attempt) {
    attempt = attempt || 0;
    return readShopifyCart().then(function (cart) {
      if (cartHasItems(cart)) return cart;
      if (attempt < CART_ATTEMPTS) {
        return sleep(220 + attempt * 260).then(function () {
          return getCart(attempt + 1);
        });
      }
      return getCartFromDom();
    }).catch(function (error) {
      if (attempt < CART_ATTEMPTS) {
        return sleep(220 + attempt * 260).then(function () {
          return getCart(attempt + 1);
        });
      }
      try {
        return getCartFromDom();
      } catch (domError) {
        throw error || domError;
      }
    });
  }

  function normalizeVariantOptions(item) {
    var optionsWithValues = Array.isArray(item.options_with_values) ? item.options_with_values : [];
    var options = optionsWithValues.map(function (option) {
      var name = String(option.name || option.title || '').trim();
      var value = String(option.value || '').trim();
      if (!name || !value || value.toLowerCase() === 'default title') return null;
      return { name: name, value: value };
    }).filter(Boolean);

    if (options.length) return options;

    var variantOptions = Array.isArray(item.variant_options) ? item.variant_options : [];
    options = variantOptions.map(function (value, index) {
      value = String(value || '').trim();
      if (!value || value.toLowerCase() === 'default title') return null;
      return { name: 'Option ' + (index + 1), value: value };
    }).filter(Boolean);

    if (options.length) return options;

    var variantTitle = String(item.variant_title || '').trim();
    if (variantTitle && variantTitle.toLowerCase() !== 'default title') {
      return variantTitle.split('/').map(function (value, index) {
        value = String(value || '').trim();
        if (!value) return null;
        return { name: 'Option ' + (index + 1), value: value };
      }).filter(Boolean);
    }

    return [];
  }

  function toCheckoutPayload(cart) {
    var items = (cart.items || []).map(function (item) {
      return {
        variantId: item.variant_id ? 'gid://shopify/ProductVariant/' + item.variant_id : null,
        title: item.product_title || item.title || 'Cart item',
        quantity: Number(item.quantity || 1),
        priceCents: Number(item.price || 0),
        finalLinePriceCents: Number(item.final_line_price || 0),
        finalPriceCents: Number(item.final_price || 0),
        image: normalizeImage(item.image),
        sku: item.sku || null,
        variantTitle: item.variant_title || null,
        options: normalizeVariantOptions(item)
      };
    }).filter(function (item) {
      /*
       * A price of nothing is a price.
       *
       * This used to require priceCents > 0, which reads as "drop anything
       * free" - and a gift-with-purchase listed in Shopify at 0 is exactly
       * that. Those lines never reached the checkout, so the gift was missing
       * from the summary and from the order.
       *
       * A line without a variant id is kept too, deliberately: the check in
       * redirectToHostedCheckout sends that cart to Shopify's own checkout
       * rather than quietly dropping a line the shopper chose.
       */
      return item.quantity > 0;
    });

    return {
      /**
       * The Shopify cart this order came from, for the merchant's tracking.
       * Attribution tools match orders to sessions by it; without it every
       * hosted-checkout order arrived unattributed.
       */
      cartToken: String(cart.token || '').trim().slice(0, 256),
      currency: (cart.currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || CONFIG.currency || 'USD').toUpperCase(),
      totalCents: Number(cart.total_price || items.reduce(function (sum, item) {
        return sum + item.priceCents * item.quantity;
      }, 0)),
      originalTotalCents: Number(cart.original_total_price || cart.items_subtotal_price || items.reduce(function (sum, item) {
        return sum + item.priceCents * item.quantity;
      }, 0)),
      shopifyDiscountCents: Number(cart.total_discount || 0),
      shopifyDiscounts: []
        .concat(cart.cart_level_discount_applications || [])
        .concat((cart.items || []).reduce(function (all, item) {
          return all.concat(item.discount_allocations || []);
        }, []))
        .map(function (discount) {
          return {
            title: String(discount.title || discount.discount_application?.title || '').slice(0, 80),
            amountCents: Number(discount.total_allocated_amount || discount.amount || 0)
          };
        })
        .filter(function (discount) { return discount.title || discount.amountCents > 0; })
        .slice(0, 10),
      itemCount: Number(cart.item_count || items.reduce(function (sum, item) {
        return sum + item.quantity;
      }, 0)),
      lineItems: items
    };
  }

  function base64UrlEncode(value) {
    var json = JSON.stringify(value);
    var encoded = btoa(unescape(encodeURIComponent(json)));
    encoded = encoded.split('+').join('-').split('/').join('_');
    while (encoded.charAt(encoded.length - 1) === '=') encoded = encoded.slice(0, -1);
    return encoded;
  }

  function redirectToHostedCheckout(cart) {
    var payload = toCheckoutPayload(cart);
    if (!payload.lineItems.length || !payload.totalCents) throw new Error('Cart is empty');

    /**
     * Every line needs a variant id, or do not go there at all.
     *
     * The hosted checkout re-prices each line against Shopify by variant id
     * before it can charge anything. A cart without them is refused on arrival
     * with a message the shopper can do nothing about — so the throw here
     * hands them to Shopify's own checkout instead, which works. Losing the
     * branded checkout for one order beats losing the order.
     */
    var missing = payload.lineItems.filter(function (item) { return !item.variantId; });
    if (missing.length) throw new Error('Cart is missing variant ids');

    var url = CHECKOUT_BASE_URL + '/checkout?cart=' + encodeURIComponent(base64UrlEncode(payload));
    window.location.assign(url);
  }

  function setButtonBusy(clicked, busy) {
    if (!clicked) return;
    if (busy) {
      clicked.setAttribute('aria-busy', 'true');
      return;
    }
    clicked.removeAttribute('aria-busy');
    if ((clicked.tagName === 'BUTTON' || clicked.tagName === 'INPUT') && clicked.dataset.hostedCheckoutWasDisabled !== 'true') {
      clicked.disabled = false;
    }
  }

  function fallbackToShopifyCheckout(reason, clicked) {
    isOpening = false;
    openingStartedAt = 0;
    console.error('Hosted checkout failed:', reason);
    setButtonBusy(clicked, false);
    window.location.assign('/checkout');
  }

  function resetOpeningState() {
    isOpening = false;
    openingStartedAt = 0;
    healCheckoutButtons();
  }

  function startHostedCheckout(evt, knownTarget) {
    if (evt && typeof evt.preventDefault === 'function') evt.preventDefault();
    if (evt && typeof evt.stopPropagation === 'function') evt.stopPropagation();
    if (evt && typeof evt.stopImmediatePropagation === 'function') evt.stopImmediatePropagation();

    if (isOpening && openingStartedAt && Date.now() - openingStartedAt > 3000) {
      resetOpeningState();
    }
    if (isOpening) return false;
    isOpening = true;
    openingStartedAt = Date.now();

    // knownTarget is passed by handleCheckoutClick, which already resolved the
    // element through composedPath(). Only re-resolve when called directly
    // (element-bound listener, or window.__startHostedCheckout).
    var clicked = (knownTarget && knownTarget.nodeType === 1) ? knownTarget : null;
    if (!clicked && evt) clicked = composedTarget(evt, 'button, input, a');
    if (clicked) {
      clicked.dataset.hostedCheckoutWasDisabled = clicked.disabled ? 'true' : 'false';
      setButtonBusy(clicked, true);
    }

    getCart()
      .then(redirectToHostedCheckout)
      .catch(function (reason) {
        fallbackToShopifyCheckout(reason, clicked);
      });

    return false;
  }

  function buttonLooksLikeReadyCheckout(el) {
    if (!el) return false;
    var text = String(el.innerText || el.value || el.getAttribute('aria-label') || '').toLowerCase();
    var hasCheckoutText =
      hasText(text, 'checkout') ||
      hasText(text, 'check out') ||
      hasText(text, 'afrekenen') ||
      hasText(text, 'pagamento') ||
      hasText(text, 'cassa') ||
      el.getAttribute('name') === 'checkout' ||
      hasText(el.className, 'upcart');
    var hasMoney = /(?:£|€|\$|gbp|eur|usd)\s*\d/i.test(text) || Boolean(el.querySelector && el.querySelector('[ymq-cart-money], .ymq-cart-app-total'));
    var cartLooksReady = queryAll(CART_READY_SELECTORS).length > 0;
    return Boolean(hasCheckoutText && (hasMoney || cartLooksReady));
  }

  function healCheckoutButtons() {
    queryAll(CHECKOUT_SELECTORS).forEach(function (el) {
      if (buttonLooksLikeReadyCheckout(el) && (el.disabled || el.getAttribute('aria-busy') === 'true')) {
        el.disabled = false;
        el.removeAttribute('aria-busy');
      }
    });
  }

  function handleCheckoutClick(evt) {
    var target = composedTarget(evt, CHECKOUT_SELECTORS);
    if (!target) return;
    return startHostedCheckout(evt, target);
  }

  function handleCartSubmit(evt) {
    var submitter = evt.submitter || document.activeElement;
    var isCheckoutSubmit = submitter && (
      (submitter.matches && submitter.matches(CHECKOUT_SELECTORS)) ||
      findCheckoutTextTarget(submitter)
    );
    var formLooksLikeCart = evt.target && evt.target.matches && evt.target.matches('form[action="/cart"], form[action$="/cart"]');
    if (!isCheckoutSubmit && (!formLooksLikeCart || !findCheckoutTextTarget(submitter))) return;
    return startHostedCheckout(evt);
  }

  function bindCheckoutButtons() {
    queryAll(CHECKOUT_SELECTORS).forEach(function (el) {
      if (buttonLooksLikeReadyCheckout(el) && el.disabled) {
        el.disabled = false;
        el.removeAttribute('aria-busy');
      }
      if (el.dataset.hostedCheckoutBound === 'true') return;
      el.dataset.hostedCheckoutBound = 'true';
      if (el.tagName === 'BUTTON' || el.tagName === 'INPUT') {
        el.setAttribute('type', 'button');
      }
      if (el.tagName === 'A') {
        el.setAttribute('href', '#hosted-checkout');
      }
      el.addEventListener('click', startHostedCheckout, true);
    });
  }

  /* ---------------------------------------------------------------------
   * UpCart shadow root observation
   * ------------------------------------------------------------------- */

  var upcartObserver = null;

  function watchUpcart() {
    var root = upcartRoot();
    if (!root || upcartObserver) return;
    // A MutationObserver on document.documentElement does not see inside a
    // shadow root, so UpCart's re-renders need their own observer.
    upcartObserver = new MutationObserver(bindCheckoutButtons);
    upcartObserver.observe(root, { childList: true, subtree: true });
    bindCheckoutButtons();
  }

  function initHostedCheckoutOverride() {
    bindCheckoutButtons();
    healCheckoutButtons();

    var observer = new MutationObserver(bindCheckoutButtons);
    observer.observe(document.documentElement, { childList: true, subtree: true });
    window.setInterval(healCheckoutButtons, 1000);

    // UpCart loads asynchronously; poll for its root for up to ~30s.
    watchUpcart();
    var tries = 0;
    var poll = window.setInterval(function () {
      if (upcartRoot()) {
        watchUpcart();
        window.clearInterval(poll);
      } else if (++tries > 60) {
        window.clearInterval(poll);
      }
    }, 500);

    // Rebind on UpCart's own lifecycle events where available.
    ['upcartSubscribeCartLoaded', 'upcartSubscribeCartOpened'].forEach(function (fn) {
      if (typeof window[fn] === 'function') {
        try { window[fn](watchUpcart); } catch (e) { /* hook unavailable */ }
      }
    });
  }

  window.__startHostedCheckout = startHostedCheckout;
  window.__whopHostedCheckoutVersion = CHECKOUT_VERSION;
  window.__resetHostedCheckout = resetOpeningState;
  window.__hostedCheckoutUpcartRoot = upcartRoot;
  document.addEventListener('click', handleCheckoutClick, true);
  document.addEventListener('submit', handleCartSubmit, true);
  window.addEventListener('pageshow', resetOpeningState);
  window.addEventListener('popstate', resetOpeningState);
  window.addEventListener('focus', resetOpeningState);
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) resetOpeningState();
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHostedCheckoutOverride);
  } else {
    initHostedCheckoutOverride();
  }
})();